import {
  DocumentSavePayload,
  DocumentSaveItem,
  DocumentSaveRequest,
  UserContext,
  ProcessedFieldItem,
  EditableField
} from "../models/document-extraction.model";

/**
 * Utility class for processing document save operations
 */
export class DocumentSaveProcessor {
  private static readonly TOKEN_COST_FIELD = "Total Token Cost";

  /**
   * Checks if a key is a score key (starts with "Score_")
   */
  private static isScoreKey(key: string): boolean {
    return key.toLowerCase().startsWith("score_");
  }

  /**
   * Finds the score key for a given field name
   */
  private static findScoreKey(
    item: any,
    fieldName: string
  ): string | undefined {
    return Object.keys(item).find(
      (key) => key.toLowerCase() === `score_${fieldName.toLowerCase()}`
    );
  }

  /**
   * Formats the data_edited field from current field states
   */
  static formatDataEdited(
    otherFields: any[],
    editableFields: EditableField[],
    tokenCost: string
  ): ProcessedFieldItem[] {
    // Process all fields with their edited values
    const formattedDataEdited = otherFields.map((item, index) => {
      const fieldName = Object.keys(item).find((k) => !this.isScoreKey(k));
      if (!fieldName) return item;

      // Get the edited value from editableFields array by matching field name
      // We need to match by field name since field_id is the database ID, not array index
      const editedField = editableFields[index];
      const editedValue =
        editedField?.value_text || editedField?.updated_text || item[fieldName];

      const scoreKey = this.findScoreKey(item, fieldName);
      const scoreValue = scoreKey ? item[scoreKey] : "100%";

      return {
        [fieldName]: editedValue,
        [scoreKey || `Score_${fieldName}`]: scoreValue
      };
    });

    // Add Total Token Cost if it exists and is not zero
    if (tokenCost && tokenCost !== "0") {
      formattedDataEdited.push({
        [this.TOKEN_COST_FIELD]: tokenCost
      });
    }

    return formattedDataEdited;
  }

  /**
   * Parses confidence score from string format (e.g., "95%" to 0.95)
   */
  private static parseConfidenceScore(scoreValue: string): number {
    if (!scoreValue) return 0;
    const numericValue = parseFloat(scoreValue.replace("%", ""));
    return isNaN(numericValue) ? 0 : numericValue / 100;
  }

  /**
   * Determines if a field value is numeric
   */
  private static isNumericValue(value: string): boolean {
    return !isNaN(parseFloat(value)) && isFinite(parseFloat(value));
  }

  /**
   * Determines if a field value is a date
   */
  private static isDateValue(value: string): boolean {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    return dateRegex.test(value) && !isNaN(Date.parse(value));
  }

  /**
   * Creates the complete payload for the save API
   */
  static createSavePayload(request: DocumentSaveRequest): DocumentSavePayload {
    const { editableFields, userContext } = request;

    // Only include fields that have been edited (have updated_text or value_text)
    const items: DocumentSaveItem[] = editableFields
      .filter((field) => {
        // Include if there's an updated_text or value_text
        return (
          (field.updated_text !== null && field.updated_text !== undefined) ||
          (field.value_text !== null && field.value_text !== undefined)
        );
      })
      .map((field) => {
        // Use updated_text if available, otherwise use value_text
        const finalValue = field.updated_text || field.value_text || "";

        return {
          field_id: field.field_id,
          updated_text: finalValue,
          extracted_updated_on: field.extracted_updated_on,
          extracted_updated_by: field.extracted_updated_by
        };
      });

    return {
      items
    };
  }

  /**
   * Validates the save request
   */
  static validateSaveRequest(request: DocumentSaveRequest): {
    isValid: boolean;
    error?: string;
  } {
    if (!request.currentConfig) {
      return {
        isValid: false,
        error: "No configuration data available"
      };
    }

    return { isValid: true };
  }
}
